import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import React from 'react';

import { BLOCK_TABS, tabs } from '@gitbook/doc-core';
import { createPalette, PalettePositionWidth, usePaletteHandle } from '@gitbook/react-uikit';
import type { Block } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { AutoSizeInput, Button, clipboard, Pressable } from '@gitbook/spine-react';
import { useMergedRefs } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { shortcuts } from '../../../Keyboard';
import { useHistoryLocation } from '../../../navigation';
import { type DraggableState, getDragItem } from '../../DragAndDrop';
import { useBlockAnchor } from '../common';
import { BlockTabsHeaderItemControls } from './BlockTabsHeaderItemControls';

/**
 * Render a tabs header item.
 */
export function BlockTabsHeaderItem(props: {
    editor: EditorContainer;
    tab: Block;
    id: string;
    index: number;
    active: boolean;
    isFirst: boolean;
    isLast: boolean;
    onSelectTab: (tab: Block) => void;
}): React.ReactElement {
    const { id, ...rest } = props;
    const { editor, tab, index, active, isFirst, isLast, onSelectTab } = props;
    const { readOnly } = editor;
    const title = tab.data ? tab.data.get('title') : 'Untitled';

    /** Drag and drop */
    const drag = useDraggable({
        disabled: readOnly,
        ...getDragItem({
            type: 'tab',
            id: tab.key,
            previewLabel: 'Move tab',
        }),
    });

    /** Insert tab */
    const onInsertTab = (increment: number) => {
        const newTab = tabs.createTabsItem('Untitled');

        editor.change((change) => {
            const parent = change.value.document.getParent(tab.key);

            if (!parent) {
                return;
            }

            change.insertNodeByKey(parent.key, index + increment, newTab);
        });

        onSelectTab(newTab);
    };

    /** Rename tab */
    const onRenameTab = (title: string) => {
        editor.change((change) => {
            change.setNodeByKey(tab.key, {
                data: tab.data.merge({
                    title,
                }),
            });
        });
    };

    /** Move tab */
    const onMoveTab = (tabId: string, position: 'before' | 'after') => {
        editor.change((change) => {
            if (position === 'before') {
                change.moveBeforeNodeByKey(tabId, tab.key);
            } else {
                change.moveAfterNodeByKey(tabId, tab.key);
            }
            const newTab = change.value.document.getDescendant(tabId);
            if (newTab) {
                change.collapseToStartOf(newTab).focus();
            }
        });
    };

    /**  Delete tab */
    const onDeleteTab = () => {
        editor.change((change) => {
            change.removeNodeByKey(tab.key);
        });
    };

    return readOnly ? (
        /*  In readonly we do not want to show the controls. */
        <TabItem
            id={id}
            title={title}
            active={active}
            isFirst={isFirst}
            isLast={isLast}
            isEditable={false}
            withOptions={false}
            editor={editor}
            tab={tab}
            onSelectTab={onSelectTab}
        />
    ) : (
        <BlockTabsHeaderItemControls
            onInsertLeft={() => onInsertTab(0)}
            onInsertRight={() => onInsertTab(1)}
            onDropBefore={(tabId) => onMoveTab(tabId, 'before')}
            onDropAfter={(tabId) => onMoveTab(tabId, 'after')}
            onDelete={onDeleteTab}
            {...rest}
        >
            <TabItem
                id={id}
                title={title}
                active={active}
                isFirst={isFirst}
                isLast={isLast}
                isEditable={active}
                onTitleChange={onRenameTab}
                editor={editor}
                tab={tab}
                withOptions
                isDragging={drag.isDragging}
                dragHandle={drag}
                onSelectTab={onSelectTab}
            />
        </BlockTabsHeaderItemControls>
    );
}

/**
 * Render a tab item.
 */
const TabItem = (props: {
    id: string;
    title: string;
    titlePlaceholder?: string;
    active?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
    isEditable: boolean;
    isDragging?: boolean;
    onTitleChange?: (title: string) => void;
    editor: EditorContainer;
    tab: Block;
    withOptions: boolean;
    dragHandle?: DraggableState;
    onSelectTab: (tab: Block) => void;
}) => {
    const {
        id,
        title,
        titlePlaceholder = 'Untitled',
        active = false,
        isFirst = false,
        isLast = false,
        isDragging = false,
        isEditable,
        withOptions,
        onTitleChange,
        editor,
        tab,
        dragHandle,
        onSelectTab,
        ...rest
    } = props;
    const [updatedTitle, setUpdatedTitle] = React.useState(title);

    const onBlur = onTitleChange
        ? () => {
              onTitleChange(updatedTitle || '');
          }
        : undefined;

    React.useEffect(() => {
        setUpdatedTitle(title);
    }, [title]);

    const location = useHistoryLocation();
    const tabURL = new URL(`${location.pathname}#${id}`, window.origin).toString();

    const paletteButtonRef = React.useRef(null);
    const buttonRef = useMergedRefs(paletteButtonRef, dragHandle?.setNodeRef);
    const paletteHandle = usePaletteHandle(paletteButtonRef);

    const onPress = () => {
        paletteHandle.open(
            createPalette({
                position: {
                    width: PalettePositionWidth.SMALL,
                },
                groups: [
                    {
                        key: 'tab',
                        commands: [
                            {
                                key: 'copy-link',
                                icon: Icon.Link,
                                title: 'Copy link',
                                trigger: () => {
                                    clipboard.writeText(tabURL, {
                                        notify: {
                                            title: 'Tab link copied to clipboard',
                                        },
                                    });
                                },
                            },
                            {
                                key: 'delete',
                                title: 'Delete',
                                icon: Icon.Trashcan,
                                trigger: () => {
                                    editor.change((change) => {
                                        tabs.removeTab(change, tab.key);
                                    });
                                },
                            },
                        ],
                    },
                ],
            })
        );
    };

    /** Tab item anchor link */
    const blockAnchor = useBlockAnchor(tab, {
        label: 'Direct link to tab',
        iconSizeClassName: 'size-3',
        onClick: () => {
            clipboard.writeText(tabURL, {
                notify: {
                    title: 'Tab link copied to clipboard',
                },
            });
        },
    });

    return (
        <div
            id={blockAnchor.blockProps.id}
            className={clsx(
                'group/tab-item flex min-w-0 max-w-full flex-initial cursor-pointer items-center border py-1',
                isFirst ? 'rounded-tl border-l' : 'border-l-transparent',
                isLast && 'rounded-tr',
                active
                    ? 'flex-none basis-auto cursor-auto border-b border-b-[var(--sp-color-bg-base)] bg-base'
                    : 'bg-muted',
                blockAnchor.blockProps.className
            )}
            {...rest}
        >
            <Pressable onPress={() => onSelectTab(tab)}>
                <div tabIndex={0} className="absolute size-full bg-transparent" />
            </Pressable>
            <span className="w-4 min-w-2 flex-shrink flex-grow basis-4" />

            {/* Title */}
            <div
                className={clsx(
                    'flex max-w-80 flex-initial items-center justify-between gap-4 py-1',
                    isDragging && 'opacity-50'
                )}
            >
                {!isEditable ? (
                    <span className="max-w-72 truncate text-muted text-ui-action transition-colors group-hover/tab-item:text-base">
                        {updatedTitle || titlePlaceholder}
                    </span>
                ) : (
                    <AutoSizeInput
                        value={updatedTitle}
                        title={updatedTitle}
                        placeholder={titlePlaceholder}
                        onChange={(event) => setUpdatedTitle(event.target.value)}
                        onBlur={onBlur}
                        onFocus={() => {
                            // blur editor when changing tab heading to prevent
                            // some keyboard shortcuts from being triggered
                            editor.change((change) => change.blur());
                        }}
                        onKeyDown={(event) => {
                            switch (event.key) {
                                case 'Enter':
                                case 'ArrowDown':
                                    event.preventDefault();
                                    // move focus to the tab body
                                    editor.change((change) =>
                                        change.focus().collapseToStartOf(tab)
                                    );
                                    break;
                                case 'ArrowUp':
                                    event.preventDefault();
                                    editor.change((change) => {
                                        change.focus();
                                        const tabs = change.value.document.getClosest(
                                            change.value.startKey!,
                                            (parent) =>
                                                'type' in parent && parent.type === BLOCK_TABS
                                        );
                                        assert(tabs, 'Could not find parent of type BLOCK_TABS');
                                        change.collapseToStartOf(tabs).moveToEndOfPreviousBlock();
                                    });
                                    break;
                            }
                        }}
                        className="max-w-72 truncate text-base text-ui-action"
                        inputClassName="placeholder:text-muted"
                    />
                )}

                {/* Anchor link */}
                {editor.readOnly && (
                    // `z-10` ensures the anchor is displayed above the Pressable which otherwise takes up the whole clickable area.
                    <span className="relative z-10 flex shrink-0 items-center">
                        {blockAnchor.anchorElement}
                    </span>
                )}
            </div>

            {withOptions ? (
                <Button
                    ref={buttonRef}
                    kind={active ? 'blank' : 'nested'}
                    size="small"
                    active={paletteHandle.opened}
                    onPress={onPress}
                    leadingIcon={Icon.GrabberVertical}
                    iconOnly
                    disabled={dragHandle?.isDragging}
                    label="Tab options"
                    tooltip={{
                        label: 'Click for tab options',
                        description: 'Drag to move',
                        shortcut: shortcuts.EDITOR_MODIFIER_PALETTE,
                        disabled: isDragging,
                    }}
                    {...dragHandle?.listeners}
                    className={clsx(
                        'z-10 ml-4 transition-opacity',
                        paletteHandle.opened || isEditable
                            ? 'opacity-100'
                            : 'opacity-0 group-hover/tab-item:opacity-100'
                    )}
                />
            ) : null}
            <span className="w-4 min-w-2 flex-shrink flex-grow basis-4" />
        </div>
    );
};

TabItem.displayName = 'TabItem';
